import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { DwellTimeChip } from "@/components/yard/DwellTimeChip";
import { TrailerBolPreviewDialog } from "@/components/trailers/TrailerBolPreviewDialog";
import { EVENT_TYPE_LABELS, LOCATION_STATUS_LABELS, type TrailerListRow } from "@/lib/trailer-filters";
import { STALE_IN_TRANSIT_DAYS, isStaleInTransit } from "@/lib/trailer-settle";

export interface LocationLookupRow {
  id: string;
  short_code: string;
  city: string | null;
}

/** Loaded / Empty / unknown badge. */
export function LoadStatusCell({ loadStatus }: { loadStatus: string | null | undefined }) {
  const ls = loadStatus?.toLowerCase();
  if (ls === "loaded") return <Badge variant="default">Loaded</Badge>;
  if (ls === "empty") return <Badge variant="outline">Empty</Badge>;
  return <span className="text-muted-foreground">—</span>;
}

/**
 * Location cell. In-transit trailers show `ORIGIN → DEST` resolved from the
 * associated order's cities; parked trailers show their site short code.
 */
export function TrailerLocationCell({
  trailer,
  allLocations,
  showCity = false,
}: {
  trailer: TrailerListRow;
  allLocations: LocationLookupRow[] | undefined;
  showCity?: boolean;
}) {
  const orders = trailer.associated_orders ?? [];

  const content = (() => {
    if (trailer.current_location_status === "in_transit") {
      const transitOrder = orders.find((o) => o.status === "in_transit") ?? orders[0];
      if (!transitOrder) return <span>—</span>;
      const findCode = (city: string | null) => {
        if (!city || !allLocations) return city ?? "—";
        const loc = allLocations.find((l) => l.city?.toLowerCase() === city.toLowerCase());
        return loc?.short_code ?? city;
      };
      return (
        <span className="text-sm font-medium">
          {findCode(transitOrder.ship_from_city)} → {findCode(transitOrder.ship_to_city)}
        </span>
      );
    }
    const sc = trailer.current_location?.short_code;
    if (!sc) return <span>—</span>;
    const city = trailer.current_location?.city;
    return <span>{showCity && city ? `${sc}: ${city}` : sc}</span>;
  })();

  return (
    <div className="flex items-center gap-1">
      {content}
      {trailer.is_customer_parking && (
        <Badge variant="outline" className="w-fit text-xs">Customer Parking</Badge>
      )}
    </div>
  );
}

export function TrailerStatusCell({
  status,
  sinceIso,
}: {
  status: string;
  /** When the trailer entered this status (last yard event). Enables the stale hint. */
  sinceIso?: string | null;
}) {
  // An `in_transit` trailer that hasn't produced a yard event in over a week
  // is almost always a missed settlement (order closed outside the driver
  // app, or delivered to a non-YMS endpoint), not a trailer still rolling.
  const stale = isStaleInTransit({ current_location_status: status, updated_at: sinceIso ?? null });
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Badge variant="outline" className="w-fit text-xs whitespace-nowrap">
        {LOCATION_STATUS_LABELS[status] ?? status}
      </Badge>
      {stale && (
        <Badge
          variant="secondary"
          className="w-fit text-xs whitespace-nowrap"
          title={`No yard event in over ${STALE_IN_TRANSIT_DAYS} days — location is likely unknown. Check the trailer in manually from Yard.`}
        >
          Stale
        </Badge>
      )}
    </div>
  );
}

export function TrailerDwellCell({ trailer }: { trailer: TrailerListRow }) {
  // Dwell is measured from the last check-in, regardless of later audits or
  // driver verifications overwriting the "latest event".
  const dwellSince =
    trailer.dwell_since ??
    (trailer.latest_event?.event_type === "checked_in" ? trailer.latest_event.occurred_at : null);
  if (trailer.current_location_status === "at_site" && dwellSince) {
    return <DwellTimeChip occurredAt={dwellSince} />;
  }
  return <span className="text-muted-foreground text-sm">—</span>;
}

export function TrailerRecentEventCell({
  event,
  align = "left",
}: {
  event: TrailerListRow["latest_event"];
  align?: "left" | "right";
}) {
  if (!event) return <span className="text-muted-foreground">—</span>;
  const isNotFound = !!event.notes?.toLowerCase().includes("not found");
  const alignCls = align === "right" ? "items-end text-right" : "";
  return (
    <div className={`flex flex-col gap-0.5 text-sm ${alignCls}`}>
      <div className="flex items-center gap-1.5">
        <span className="font-medium">{EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}</span>
        {event.event_type === "audited" && event.notes && (
          isNotFound ? (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Not found</Badge>
          ) : (
            <Badge variant="info" className="text-[10px] px-1.5 py-0">Found</Badge>
          )
        )}
      </div>
      <span className="text-muted-foreground text-xs">
        {format(new Date(event.occurred_at), "MMM d, h:mm a")}
        {event.location?.short_code ? ` · ${event.location.short_code}` : ""}
      </span>
    </div>
  );
}

export function TrailerAssociatedOrdersCell({
  orders,
  inline = false,
}: {
  orders: TrailerListRow["associated_orders"];
  inline?: boolean;
}) {
  const navigate = useNavigate();
  if (!orders?.length) return <span className="text-muted-foreground">—</span>;
  const priority: Record<string, number> = { in_transit: 0, booked: 1, confirmed: 2 };
  const sorted = [...orders].sort((a, b) => (priority[a.status] ?? 99) - (priority[b.status] ?? 99));
  const first = sorted[0];
  const remaining = orders.length - 1;
  return (
    <div className={inline ? "flex items-center gap-1.5" : "flex flex-col gap-0.5"}>
      <button
        onClick={(e) => { e.stopPropagation(); navigate(`/orders/${first.id}`); }}
        className="text-xs font-medium text-primary hover:underline text-left"
      >
        {first.unique_id}
      </button>
      {remaining > 0 && <span className="text-xs text-muted-foreground">+{remaining} more</span>}
    </div>
  );
}

/** Documents cell — opens the BOL preview when the trailer has check-in docs. */
export function TrailerDocumentsCell({ trailer }: { trailer: TrailerListRow }) {
  const docCount = trailer.doc_count ?? 0;
  if (docCount <= 0) return <span className="text-muted-foreground text-sm">—</span>;
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <TrailerBolPreviewDialog
        trailerId={trailer.id}
        trailerLabel={trailer.trailer_id}
        docCount={docCount}
      />
    </div>
  );
}
